import { useEffect, useState } from 'react';
import type { z } from 'zod';
import { authFetch } from '../../utils/api';
import { ANNOUNCEMENT_BRAND } from '../../schemas/announcement';

export class AnnouncementRequestError extends Error {
  constructor(message: string, readonly status: number, readonly errorCode?: string, readonly ambiguous = false) { super(message); }
}
type Fetcher = (path: string, options?: RequestInit) => Promise<Response>;
export async function announcementRequest<T>(path: string, schema: z.ZodType<T, z.ZodTypeDef, unknown>, options?: RequestInit, fetcher: Fetcher = authFetch): Promise<T> {
  let response: Response;
  try { response = await fetcher(path, { cache: 'no-store', ...options }); }
  catch (error) { if (options?.signal?.aborted) throw error; throw new AnnouncementRequestError('未收到伺服器確認，請保留原請求並重試。', 0, undefined, true); }
  let result: unknown;
  try { result = await response.json(); } catch { throw new AnnouncementRequestError('伺服器回覆格式無法確認，請重新查詢。', response.status, undefined, true); }
  const envelope = result as { code?: unknown; msg?: unknown; data?: { errorCode?: unknown } } | null;
  if (!response.ok || envelope?.code !== 0) {
    const status = !response.ok ? response.status : typeof envelope?.code === 'number' ? envelope.code : 0;
    throw new AnnouncementRequestError(typeof envelope?.msg === 'string' ? envelope.msg : '公告請求失敗', status, typeof envelope?.data?.errorCode === 'string' ? envelope.data.errorCode : undefined, status === 0 || status >= 500);
  }
  const parsed = schema.safeParse(envelope.data);
  if (!parsed.success) throw new AnnouncementRequestError('公告資料不完整，請重新查詢。', response.status, undefined, true);
  return parsed.data;
}
export function announcementPath(suffix = '', query: Record<string, string | number | undefined> = {}) {
  const params = new URLSearchParams({ brand: ANNOUNCEMENT_BRAND });
  for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== '') params.set(key, String(value));
  return `/api/admin/announcements${suffix}?${params}`;
}
export function announcementWrite(values: object, method = 'POST'): RequestInit {
  return { method, headers: { 'X-Target-Brand': ANNOUNCEMENT_BRAND }, body: JSON.stringify({ ...values, targetBrand: ANNOUNCEMENT_BRAND }) };
}
export function errorMessage(error: unknown) { return error instanceof Error ? error.message : '公告操作失敗'; }
export function useAnnouncementRead<T>(path: string | null, schema: z.ZodType<T, z.ZodTypeDef, unknown>, refresh = 0, context = '') {
  const key = `${path}:${refresh}:${context}`;
  const [state, setState] = useState<{ key: string; data: T | null; error: AnnouncementRequestError | null; loading: boolean }>();
  useEffect(() => {
    if (!path) return;
    const controller = new AbortController();
    setState({ key, data: null, error: null, loading: true });
    announcementRequest(path, schema, { signal: controller.signal }).then(data => {
      if (!controller.signal.aborted) setState({ key, data, error: null, loading: false });
    }).catch(error => {
      if (!controller.signal.aborted) setState({ key, data: null, error: error instanceof AnnouncementRequestError ? error : new AnnouncementRequestError(errorMessage(error), 0), loading: false });
    });
    return () => controller.abort();
  }, [path, schema, key]);
  return state?.key === key ? state : { data: null, error: null, loading: !!path };
}
